// ─────────────────────────────────────────────────────────────────────
// i18n setup — customer-facing localisation only.
//
// • English (`en`) is the source of truth and the fallback for every
//   missing key. We ship `en/*.json` inline so the customer never sees
//   a flash of empty UI even if the network is slow.
// • Other languages are imported eagerly too (small total payload,
//   ~50–80 KB gzipped for 7 langs × 11 namespaces — cheaper than the
//   complexity of async loading + suspense fallbacks).
// • Detection order: explicit `i18n.changeLanguage()` → localStorage
//   → browser navigator → fallback `en`. When the user logs in we
//   also pull `user.language` from the backend and call
//   `i18n.changeLanguage()` once.
//
// NOTHING in this file talks to the backend directly — that lives in
// LanguageSwitcher / AuthContext bootstrap.
// ─────────────────────────────────────────────────────────────────────

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ── English (default + fallback) ────────────────────────────────────
import enCommon       from './locales/en/common.json';
import enAuth         from './locales/en/auth.json';
import enDashboard    from './locales/en/dashboard.json';
import enOrders       from './locales/en/orders.json';
import enNewOrder     from './locales/en/new-order.json';
import enProfile      from './locales/en/profile.json';
import enTransactions from './locales/en/transactions.json';
import enRecharge     from './locales/en/recharge.json';
import enTickets      from './locales/en/tickets.json';
import enOriginalFiles from './locales/en/original-files.json';
import enValidation   from './locales/en/validation.json';
import enEvc          from './locales/en/evc.json';

// ── Italian ────────────────────────────────────────────────────────
import itCommon       from './locales/it/common.json';
import itAuth         from './locales/it/auth.json';
import itDashboard    from './locales/it/dashboard.json';
import itOrders       from './locales/it/orders.json';
import itNewOrder     from './locales/it/new-order.json';
import itProfile      from './locales/it/profile.json';
import itTransactions from './locales/it/transactions.json';
import itRecharge     from './locales/it/recharge.json';
import itTickets      from './locales/it/tickets.json';
import itOriginalFiles from './locales/it/original-files.json';
import itValidation   from './locales/it/validation.json';
import itEvc          from './locales/it/evc.json';

// ── Simplified Chinese ─────────────────────────────────────────────
import zhCommon       from './locales/zh-CN/common.json';
import zhAuth         from './locales/zh-CN/auth.json';
import zhDashboard    from './locales/zh-CN/dashboard.json';
import zhOrders       from './locales/zh-CN/orders.json';
import zhNewOrder     from './locales/zh-CN/new-order.json';
import zhProfile      from './locales/zh-CN/profile.json';
import zhTransactions from './locales/zh-CN/transactions.json';
import zhRecharge     from './locales/zh-CN/recharge.json';
import zhTickets      from './locales/zh-CN/tickets.json';
import zhOriginalFiles from './locales/zh-CN/original-files.json';
import zhValidation   from './locales/zh-CN/validation.json';
import zhEvc          from './locales/zh-CN/evc.json';

// ── Spanish ────────────────────────────────────────────────────────
import esCommon       from './locales/es/common.json';
import esAuth         from './locales/es/auth.json';
import esDashboard    from './locales/es/dashboard.json';
import esOrders       from './locales/es/orders.json';
import esNewOrder     from './locales/es/new-order.json';
import esProfile      from './locales/es/profile.json';
import esTransactions from './locales/es/transactions.json';
import esRecharge     from './locales/es/recharge.json';
import esTickets      from './locales/es/tickets.json';
import esOriginalFiles from './locales/es/original-files.json';
import esValidation   from './locales/es/validation.json';
import esEvc          from './locales/es/evc.json';

// ── German ─────────────────────────────────────────────────────────
import deCommon       from './locales/de/common.json';
import deAuth         from './locales/de/auth.json';
import deDashboard    from './locales/de/dashboard.json';
import deOrders       from './locales/de/orders.json';
import deNewOrder     from './locales/de/new-order.json';
import deProfile      from './locales/de/profile.json';
import deTransactions from './locales/de/transactions.json';
import deRecharge     from './locales/de/recharge.json';
import deTickets      from './locales/de/tickets.json';
import deOriginalFiles from './locales/de/original-files.json';
import deValidation   from './locales/de/validation.json';
import deEvc          from './locales/de/evc.json';

// ── French ─────────────────────────────────────────────────────────
import frCommon       from './locales/fr/common.json';
import frAuth         from './locales/fr/auth.json';
import frDashboard    from './locales/fr/dashboard.json';
import frOrders       from './locales/fr/orders.json';
import frNewOrder     from './locales/fr/new-order.json';
import frProfile      from './locales/fr/profile.json';
import frTransactions from './locales/fr/transactions.json';
import frRecharge     from './locales/fr/recharge.json';
import frTickets      from './locales/fr/tickets.json';
import frOriginalFiles from './locales/fr/original-files.json';
import frValidation   from './locales/fr/validation.json';
import frEvc          from './locales/fr/evc.json';

// ── Portuguese ─────────────────────────────────────────────────────
import ptCommon       from './locales/pt/common.json';
import ptAuth         from './locales/pt/auth.json';
import ptDashboard    from './locales/pt/dashboard.json';
import ptOrders       from './locales/pt/orders.json';
import ptNewOrder     from './locales/pt/new-order.json';
import ptProfile      from './locales/pt/profile.json';
import ptTransactions from './locales/pt/transactions.json';
import ptRecharge     from './locales/pt/recharge.json';
import ptTickets      from './locales/pt/tickets.json';
import ptOriginalFiles from './locales/pt/original-files.json';
import ptValidation   from './locales/pt/validation.json';
import ptEvc          from './locales/pt/evc.json';

// ── Czech (Phase H14) ──────────────────────────────────────────────
import csCommon       from './locales/cs/common.json';
import csAuth         from './locales/cs/auth.json';
import csDashboard    from './locales/cs/dashboard.json';
import csOrders       from './locales/cs/orders.json';
import csNewOrder     from './locales/cs/new-order.json';
import csProfile      from './locales/cs/profile.json';
import csTransactions from './locales/cs/transactions.json';
import csRecharge     from './locales/cs/recharge.json';
import csTickets      from './locales/cs/tickets.json';
import csOriginalFiles from './locales/cs/original-files.json';
import csValidation   from './locales/cs/validation.json';
import csEvc          from './locales/cs/evc.json';

// ── Russian (Phase H14) ────────────────────────────────────────────
import ruCommon       from './locales/ru/common.json';
import ruAuth         from './locales/ru/auth.json';
import ruDashboard    from './locales/ru/dashboard.json';
import ruOrders       from './locales/ru/orders.json';
import ruNewOrder     from './locales/ru/new-order.json';
import ruProfile      from './locales/ru/profile.json';
import ruTransactions from './locales/ru/transactions.json';
import ruRecharge     from './locales/ru/recharge.json';
import ruTickets      from './locales/ru/tickets.json';
import ruOriginalFiles from './locales/ru/original-files.json';
import ruValidation   from './locales/ru/validation.json';
import ruEvc          from './locales/ru/evc.json';

// ── Polish (Phase H15) ─────────────────────────────────────────────
import plCommon       from './locales/pl/common.json';
import plAuth         from './locales/pl/auth.json';
import plDashboard    from './locales/pl/dashboard.json';
import plOrders       from './locales/pl/orders.json';
import plNewOrder     from './locales/pl/new-order.json';
import plProfile      from './locales/pl/profile.json';
import plTransactions from './locales/pl/transactions.json';
import plRecharge     from './locales/pl/recharge.json';
import plTickets      from './locales/pl/tickets.json';
import plOriginalFiles from './locales/pl/original-files.json';
import plValidation   from './locales/pl/validation.json';
import plEvc          from './locales/pl/evc.json';

// ── Hungarian (Phase H22 — bootstrapped from EN, new-order fully translated) ──
import huCommon       from './locales/hu/common.json';
import huAuth         from './locales/hu/auth.json';
import huDashboard    from './locales/hu/dashboard.json';
import huOrders       from './locales/hu/orders.json';
import huNewOrder     from './locales/hu/new-order.json';
import huProfile      from './locales/hu/profile.json';
import huTransactions from './locales/hu/transactions.json';
import huRecharge     from './locales/hu/recharge.json';
import huTickets      from './locales/hu/tickets.json';
import huOriginalFiles from './locales/hu/original-files.json';
import huValidation   from './locales/hu/validation.json';
import huEvc          from './locales/hu/evc.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en',    label: 'English',    flag: '🇬🇧', native: 'English' },
  { code: 'it',    label: 'Italian',    flag: '🇮🇹', native: 'Italiano' },
  { code: 'zh-CN', label: 'Chinese',    flag: '🇨🇳', native: '简体中文' },
  { code: 'es',    label: 'Spanish',    flag: '🇪🇸', native: 'Español' },
  { code: 'de',    label: 'German',     flag: '🇩🇪', native: 'Deutsch' },
  { code: 'fr',    label: 'French',     flag: '🇫🇷', native: 'Français' },
  { code: 'pt',    label: 'Portuguese', flag: '🇵🇹', native: 'Português' },
  { code: 'cs',    label: 'Czech',      flag: '🇨🇿', native: 'Čeština' },
  { code: 'ru',    label: 'Russian',    flag: '🇷🇺', native: 'Русский' },
  { code: 'pl',    label: 'Polish',     flag: '🇵🇱', native: 'Polski' },
  { code: 'hu',    label: 'Hungarian',  flag: '🇭🇺', native: 'Magyar' },
];

const resources = {
  en:      { common: enCommon,    auth: enAuth,    dashboard: enDashboard,    orders: enOrders,    'new-order': enNewOrder,    profile: enProfile,    transactions: enTransactions,    recharge: enRecharge,    tickets: enTickets,    'original-files': enOriginalFiles,    validation: enValidation,    evc: enEvc },
  it:      { common: itCommon,    auth: itAuth,    dashboard: itDashboard,    orders: itOrders,    'new-order': itNewOrder,    profile: itProfile,    transactions: itTransactions,    recharge: itRecharge,    tickets: itTickets,    'original-files': itOriginalFiles,    validation: itValidation,    evc: itEvc },
  'zh-CN': { common: zhCommon,    auth: zhAuth,    dashboard: zhDashboard,    orders: zhOrders,    'new-order': zhNewOrder,    profile: zhProfile,    transactions: zhTransactions,    recharge: zhRecharge,    tickets: zhTickets,    'original-files': zhOriginalFiles,    validation: zhValidation,    evc: zhEvc },
  // Alias `zh` → same Chinese bundle so browser auto-detection of region-less
  // `zh` still localizes the UI (we no longer use nonExplicitSupportedLngs).
  zh:      { common: zhCommon,    auth: zhAuth,    dashboard: zhDashboard,    orders: zhOrders,    'new-order': zhNewOrder,    profile: zhProfile,    transactions: zhTransactions,    recharge: zhRecharge,    tickets: zhTickets,    'original-files': zhOriginalFiles,    validation: zhValidation,    evc: zhEvc },
  es:      { common: esCommon,    auth: esAuth,    dashboard: esDashboard,    orders: esOrders,    'new-order': esNewOrder,    profile: esProfile,    transactions: esTransactions,    recharge: esRecharge,    tickets: esTickets,    'original-files': esOriginalFiles,    validation: esValidation,    evc: esEvc },
  de:      { common: deCommon,    auth: deAuth,    dashboard: deDashboard,    orders: deOrders,    'new-order': deNewOrder,    profile: deProfile,    transactions: deTransactions,    recharge: deRecharge,    tickets: deTickets,    'original-files': deOriginalFiles,    validation: deValidation,    evc: deEvc },
  fr:      { common: frCommon,    auth: frAuth,    dashboard: frDashboard,    orders: frOrders,    'new-order': frNewOrder,    profile: frProfile,    transactions: frTransactions,    recharge: frRecharge,    tickets: frTickets,    'original-files': frOriginalFiles,    validation: frValidation,    evc: frEvc },
  pt:      { common: ptCommon,    auth: ptAuth,    dashboard: ptDashboard,    orders: ptOrders,    'new-order': ptNewOrder,    profile: ptProfile,    transactions: ptTransactions,    recharge: ptRecharge,    tickets: ptTickets,    'original-files': ptOriginalFiles,    validation: ptValidation,    evc: ptEvc },
  cs:      { common: csCommon,    auth: csAuth,    dashboard: csDashboard,    orders: csOrders,    'new-order': csNewOrder,    profile: csProfile,    transactions: csTransactions,    recharge: csRecharge,    tickets: csTickets,    'original-files': csOriginalFiles,    validation: csValidation,    evc: csEvc },
  ru:      { common: ruCommon,    auth: ruAuth,    dashboard: ruDashboard,    orders: ruOrders,    'new-order': ruNewOrder,    profile: ruProfile,    transactions: ruTransactions,    recharge: ruRecharge,    tickets: ruTickets,    'original-files': ruOriginalFiles,    validation: ruValidation,    evc: ruEvc },
  pl:      { common: plCommon,    auth: plAuth,    dashboard: plDashboard,    orders: plOrders,    'new-order': plNewOrder,    profile: plProfile,    transactions: plTransactions,    recharge: plRecharge,    tickets: plTickets,    'original-files': plOriginalFiles,    validation: plValidation,    evc: plEvc },
  hu:      { common: huCommon,    auth: huAuth,    dashboard: huDashboard,    orders: huOrders,    'new-order': huNewOrder,    profile: huProfile,    transactions: huTransactions,    recharge: huRecharge,    tickets: huTickets,    'original-files': huOriginalFiles,    validation: huValidation,    evc: huEvc },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES.map((l) => l.code), 'zh'],
    // NOTE: do NOT enable `nonExplicitSupportedLngs` — when combined with the
    // region-coded `zh-CN` resource key it caused i18next to silently fall
    // back to `en`. We instead alias the region-less code under `resources`
    // (e.g. `zh -> zh-CN`) below so browser auto-detection still works.
    load: 'currentOnly',           // Don't strip region — keep `zh-CN` intact.
    lowerCaseLng: false,           // Preserve the `CN` casing of `zh-CN`.
    cleanCode: false,              // Don't normalize/clean — match resource keys exactly.
    defaultNS: 'common',
    ns: ['common', 'auth', 'dashboard', 'orders', 'new-order', 'profile', 'transactions', 'recharge', 'tickets', 'original-files', 'validation', 'evc'],
    interpolation: {
      escapeValue: false, // React handles escaping.
    },
    detection: {
      // Persist explicit user choice in localStorage. The backend
      // `user.language` field overrides this after login (handled by
      // AuthContext bootstrap).
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'autoremap.lang',
    },
    returnEmptyString: false,
    returnNull: false,
  });

export default i18n;
